import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { useMetaOfferAllowedInCountry } from '@percent/cause-dashboard/common/hooks/useMetaOfferAllowedInCountry/useMetaOfferAllowedInCountry'
import type { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'

export const MAX_PARTNER_LOGOS = 5

export type PartnerLogo = {
  partner: SmartApplyPartner
  logo: string
}

/**
 * Alphabetical, matching how the card logos are sorted. Above five partners the last one is dropped —
 * today Zoom. Reorder by priority if which five show matters.
 */
const CARD_LOGOS: PartnerLogo[] = [
  {
    partner: 'adobe',
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Adobe.png')
  },
  {
    partner: 'adobeExpress',
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png')
  },
  { partner: 'claude', logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Claude.png') },
  { partner: 'lovable', logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Lovable.png') },
  { partner: 'meta', logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/meta.png') },
  { partner: 'openai', logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Openai.png') },
  { partner: 'zoom', logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Zoom.webp') }
]

/** Logos to show on the graphic. Same flag gates as the cards, so the two can't disagree. */
export function useSmartApplyPartnerLogos(): PartnerLogo[] {
  const isClaudeEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_CLAUDE_SMART_APPLY)
  const isLovableEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_LOVABLE_SMART_APPLY)
  const isMetaEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY)
  const isMetaAllowedInCountry = useMetaOfferAllowedInCountry(isMetaEnabled)
  const isAdobeExpressEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_ADOBE_EXPRESS_SECOND_SMART_APPLY)

  const isEnabled: Partial<Record<SmartApplyPartner, boolean>> = {
    adobeExpress: isAdobeExpressEnabled,
    claude: isClaudeEnabled,
    lovable: isLovableEnabled,
    meta: isMetaEnabled && isMetaAllowedInCountry
  }

  return CARD_LOGOS.filter(({ partner }) => isEnabled[partner] ?? true).slice(0, MAX_PARTNER_LOGOS)
}
